import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Box,
    Typography,
    Slider,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Divider,
    Stack,
    IconButton,
    Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
    Help as HelpIcon
} from '@mui/icons-material';

interface SearchFilters {
    query: string;
    fileTypes: string[];
    categories: string[];
    tags: string[];
    dateFrom?: Date | null;
    dateTo?: Date | null;
    sizeMin?: number;
    sizeMax?: number;
    confidenceMin?: number;
    hasOCR?: boolean;
    department?: string;
    sharedWithMe?: boolean;
    myDocuments?: boolean;
    excludeSensitive?: boolean;
    onlySensitive?: boolean;
}

interface AdvancedSearchProps {
    open: boolean;
    onClose: () => void;
    onSearch: (filters: SearchFilters) => void;
    initialFilters?: Partial<SearchFilters>;
    availableTags?: string[];
    availableDepartments?: string[];
}

const fileTypeOptions = [
    { value: 'pdf', label: 'PDF Documents' },
    { value: 'doc', label: 'Word Documents' },
    { value: 'xls', label: 'Spreadsheets' },
    { value: 'image', label: 'Images' },
    { value: 'text', label: 'Text Files' },
    { value: 'email', label: 'Emails' }
];

const categoryOptions = [
    { value: 'contracts', label: 'Contracts' },
    { value: 'invoices', label: 'Invoices' },
    { value: 'reports', label: 'Reports' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'forms', label: 'Forms' },
    { value: 'other', label: 'Other' }
];

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
    open,
    onClose,
    onSearch,
    initialFilters = {},
    availableTags = [],
    availableDepartments = []
}) => {
    const [filters, setFilters] = useState<SearchFilters>({
        query: '',
        fileTypes: [],
        categories: [],
        tags: [],
        dateFrom: null,
        dateTo: null,
        sizeMin: 0,
        sizeMax: 100,
        confidenceMin: 0,
        hasOCR: false,
        department: '',
        sharedWithMe: false,
        myDocuments: false,
        excludeSensitive: false,
        onlySensitive: false,
        ...initialFilters
    });

    const handleReset = () => {
        setFilters({
            query: '',
            fileTypes: [],
            categories: [],
            tags: [],
            dateFrom: null,
            dateTo: null,
            sizeMin: 0,
            sizeMax: 100,
            confidenceMin: 0,
            hasOCR: false,
            department: '',
            sharedWithMe: false,
            myDocuments: false,
            excludeSensitive: false,
            onlySensitive: false
        });
    };

    const handleSearch = () => {
        onSearch(filters);
        onClose();
    };

    const formatFileSize = (mb: number): string => {
        if (mb >= 1000) {
            return `${(mb / 1000).toFixed(1)} GB`;
        }
        return `${mb} MB`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterIcon />
                        <Typography variant="h6">Advanced Search</Typography>
                    </Box>
                    <Tooltip title="Search tips">
                        <IconButton size="small">
                            <HelpIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Full-text search */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Search Text
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter keywords to search in document content..."
                            value={filters.query}
                            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Use quotes for exact phrases, + for required words, - for excluded words
                        </Typography>
                    </Box>

                    <Divider />

                    {/* File types and categories */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Typography variant="subtitle1" gutterBottom>
                                File Types
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {fileTypeOptions.map((type) => (
                                    <Chip
                                        key={type.value}
                                        label={type.label}
                                        onClick={() => {
                                            const newTypes = filters.fileTypes.includes(type.value)
                                                ? filters.fileTypes.filter(t => t !== type.value)
                                                : [...filters.fileTypes, type.value];
                                            setFilters({ ...filters, fileTypes: newTypes });
                                        }}
                                        color={filters.fileTypes.includes(type.value) ? 'primary' : 'default'}
                                        variant={filters.fileTypes.includes(type.value) ? 'filled' : 'outlined'}
                                    />
                                ))}
                            </Box>
                        </div>

                        <div>
                            <Typography variant="subtitle1" gutterBottom>
                                Categories
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {categoryOptions.map((cat) => (
                                    <Chip
                                        key={cat.value}
                                        label={cat.label}
                                        onClick={() => {
                                            const newCats = filters.categories.includes(cat.value)
                                                ? filters.categories.filter(c => c !== cat.value)
                                                : [...filters.categories, cat.value];
                                            setFilters({ ...filters, categories: newCats });
                                        }}
                                        color={filters.categories.includes(cat.value) ? 'primary' : 'default'}
                                        variant={filters.categories.includes(cat.value) ? 'filled' : 'outlined'}
                                    />
                                ))}
                            </Box>
                        </div>
                    </div>

                    <Divider />

                    {/* Tags */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Tags
                        </Typography>
                        <Autocomplete
                            multiple
                            options={availableTags}
                            value={filters.tags}
                            onChange={(_, newValue) => setFilters({ ...filters, tags: newValue })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select or type tags..."
                                    variant="outlined"
                                />
                            )}
                            renderTags={(value: string[], getTagProps) =>
                                value.map((option: string, index: number) => (
                                    <Chip
                                        variant="outlined"
                                        label={option}
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                        />
                    </Box>

                    <Divider />

                    {/* Date range */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Date Range
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <DatePicker
                                        label="From Date"
                                        value={filters.dateFrom}
                                        onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </div>
                                <div>
                                    <DatePicker
                                        label="To Date"
                                        value={filters.dateTo}
                                        onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </div>
                            </div>
                        </LocalizationProvider>
                    </Box>

                    <Divider />

                    {/* File size range */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            File Size Range
                        </Typography>
                        <Box sx={{ px: 2 }}>
                            <Slider
                                value={[filters.sizeMin || 0, filters.sizeMax || 100]}
                                onChange={(_, newValue) => {
                                    const [min, max] = newValue as number[];
                                    setFilters({ ...filters, sizeMin: min, sizeMax: max });
                                }}
                                valueLabelDisplay="auto"
                                valueLabelFormat={formatFileSize}
                                max={1000}
                                marks={[
                                    { value: 0, label: '0 MB' },
                                    { value: 250, label: '250 MB' },
                                    { value: 500, label: '500 MB' },
                                    { value: 750, label: '750 MB' },
                                    { value: 1000, label: '1 GB' }
                                ]}
                            />
                        </Box>
                    </Box>

                    <Divider />

                    {/* OCR confidence */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            OCR Confidence
                        </Typography>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.hasOCR}
                                            onChange={(e) => setFilters({ ...filters, hasOCR: e.target.checked })}
                                        />
                                    }
                                    label="Only documents with OCR"
                                />
                            </div>
                            <div>
                                {filters.hasOCR && (
                                    <Box sx={{ px: 2 }}>
                                        <Slider
                                            value={filters.confidenceMin || 0}
                                            onChange={(_, newValue) => 
                                                setFilters({ ...filters, confidenceMin: newValue as number })
                                            }
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => `${value}%`}
                                            marks={[
                                                { value: 0, label: '0%' },
                                                { value: 50, label: '50%' },
                                                { value: 100, label: '100%' }
                                            ]}
                                        />
                                    </Box>
                                )}
                            </div>
                        </div>
                    </Box>

                    <Divider />

                    {/* Access filters */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Access Filters
                        </Typography>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.myDocuments}
                                            onChange={(e) => setFilters({ ...filters, myDocuments: e.target.checked })}
                                        />
                                    }
                                    label="My Documents"
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.sharedWithMe}
                                            onChange={(e) => setFilters({ ...filters, sharedWithMe: e.target.checked })}
                                        />
                                    }
                                    label="Shared with Me"
                                />
                            </div>
                            <div>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Department</InputLabel>
                                    <Select
                                        value={filters.department}
                                        label="Department"
                                        onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                                    >
                                        <MenuItem value="">All Departments</MenuItem>
                                        {availableDepartments.map((dept) => (
                                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    </Box>

                    <Divider />

                    {/* Sensitive data filters */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Sensitive Data Filters
                        </Typography>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.excludeSensitive}
                                            onChange={(e) => {
                                                setFilters({ 
                                                    ...filters, 
                                                    excludeSensitive: e.target.checked,
                                                    onlySensitive: e.target.checked ? false : filters.onlySensitive
                                                });
                                            }}
                                        />
                                    }
                                    label="Exclude Sensitive Documents"
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.onlySensitive}
                                            onChange={(e) => {
                                                setFilters({ 
                                                    ...filters, 
                                                    onlySensitive: e.target.checked,
                                                    excludeSensitive: e.target.checked ? false : filters.excludeSensitive
                                                });
                                            }}
                                        />
                                    }
                                    label="Only Sensitive Documents"
                                />
                            </div>
                        </div>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleReset} startIcon={<ClearIcon />}>
                    Reset
                </Button>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSearch}
                    variant="contained"
                    startIcon={<SearchIcon />}
                >
                    Search
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdvancedSearch;